import { AlertTriangle, Clock } from "lucide-react";
import StatusBadge from "./StatusBadge";
import type { StatusIncident } from "@/api/client";

interface IncidentTimelineProps {
  incidents: StatusIncident[];
  title?: string;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function impactColor(impact: string): string {
  switch (impact) {
    case "critical":
      return "text-red-600";
    case "major":
      return "text-orange-600";
    case "minor":
      return "text-amber-600";
    default:
      return "text-gray-600";
  }
}

export default function IncidentTimeline({
  incidents,
  title = "Incidents",
}: IncidentTimelineProps) {
  if (incidents.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        No incidents to display.
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-gray-900">{title}</h2>
      <div className="space-y-6">
        {incidents.map((incident) => (
          <div key={incident.id} className="card overflow-hidden">
            {/* Incident header */}
            <div className="border-b border-gray-100 bg-gray-50/50 px-4 py-3 sm:px-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <h3 className="text-sm font-medium text-gray-900">
                    {incident.title}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-medium capitalize ${impactColor(incident.impact)}`}
                  >
                    {incident.impact}
                  </span>
                  <StatusBadge status={incident.status} />
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-400">
                {formatDate(incident.created_at)}
              </p>
            </div>

            {/* Timeline updates */}
            {incident.updates.length > 0 && (
              <div className="px-4 py-3 sm:px-6">
                <div className="relative space-y-4">
                  {/* Vertical line */}
                  <div
                    className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200"
                    aria-hidden="true"
                  />

                  {incident.updates.map((update, idx) => (
                    <div key={idx} className="relative flex gap-3 pl-6">
                      {/* Dot */}
                      <div
                        className="absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full border-2 border-white bg-gray-300"
                        aria-hidden="true"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <StatusBadge status={update.status} />
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="h-3 w-3" />
                            {formatDateTime(update.created_at)}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-700">
                          {update.message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
